import React, { useState, useRef, useEffect } from 'react';
import { deleteFood, fetchFoods, addDeletedCustomFood, getLocalFoods } from "../utils";
import { auth } from "../../firebase";

const Alimentos = ({ alimentosComunes, alimentosPersonalizados, setAlimentosPersonalizados, onAddCustomFood, hiddenCommonFoods = [], onHideCommonFood, onShowCommonFood, vistaActual }) => {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [nuevoAlimento, setNuevoAlimento] = useState({ nombre: '', carbsPor100: '' });
  const [editandoAlimento, setEditandoAlimento] = useState(null); // {nombre, carbsPor100, tipo}
  const [sortBy, setSortBy] = useState('nombre');
  const [sortDir, setSortDir] = useState('asc');
  const [borrando, setBorrando] = useState({}); // {nombre: true}
  const [showTick, setShowTick] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, foodId: null, nombre: '' });
  const [movingFood, setMovingFood] = useState(null); // nombre del alimento en animación
  const [editandoFila, setEditandoFila] = useState(null); // nombre del alimento en edición
  const [editFilaData, setEditFilaData] = useState({ nombre: '', carbsPor100: '' });
  const rowRefs = useRef({});

  const abrirModalNuevoAlimento = () => {
    setNuevoAlimento({ nombre: '', carbsPor100: '' });
    setModalAbierto(true);
  };

  // Validar y guardar un alimento personalizado
  const guardarAlimentoPersonalizado = async () => {
    if (!nuevoAlimento.nombre || !nuevoAlimento.carbsPor100) {
      alert("Por favor, completa el nombre y los hidratos de carbono por 100g.");
      return;
    }
    const food = {
      nombre: nuevoAlimento.nombre,
      carbsPor100: parseFloat(nuevoAlimento.carbsPor100),
    };
    // Comprobar si ya existe exactamente igual (nombre y HC/100g)
    const existsExact = [...alimentosPersonalizados, ...alimentosComunes].some(a => a.nombre.toLowerCase() === food.nombre.toLowerCase() && Number(a.carbsPor100) === Number(food.carbsPor100));
    if (existsExact) {
      setModalAbierto(false); // Cierra antes de mostrar splash
      setShowTick('already-exists');
      setTimeout(() => setShowTick(false), 1500);
      return;
    }
    try {
      setModalAbierto(false); // Cierra antes de mostrar splash
      await onAddCustomFood(food);
      setNuevoAlimento({ nombre: '', carbsPor100: '' });
      setShowTick('saved');
      setTimeout(() => setShowTick(false), 1000);
    } catch (error) {
      console.error("Error al guardar el alimento:", error);
      alert("Hubo un error al guardar el alimento. Inténtalo de nuevo más tarde.");
    }
  };

  // Eliminar un alimento personalizado
  const eliminarAlimentoPersonalizado = async (food) => {
    setConfirmDelete({ open: true, foodId: food, nombre: food.nombre });
  };

  // Confirmar borrado personalizado
  const confirmarBorradoPersonalizado = async () => {
    const alimento = confirmDelete.foodId;
    const foodId = `${auth.currentUser.uid}_${alimento.nombre.toLowerCase()}`;
    try {
      await deleteFood(foodId);
      // Añadir a la lista de eliminados (por nombre)
      if (alimento && alimento.nombre) {
        addDeletedCustomFood(alimento.nombre);
      }
      const nuevosAlimentos = alimentosPersonalizados.filter(a => a.nombre !== alimento.nombre);
      setAlimentosPersonalizados(nuevosAlimentos);
    } catch (error) {
      console.error("Error al eliminar el alimento:", error);
      alert("Hubo un error al eliminar el alimento. Inténtalo de nuevo más tarde.");
    }
    setConfirmDelete({ open: false, foodId: null, nombre: '' });
  };

  // Cancelar borrado
  const cancelarBorrado = () => {
    setConfirmDelete({ open: false, foodId: null, nombre: '' });
  };

  // Editar alimento personalizado
  const abrirEditarAlimento = (alimento) => {
    setEditandoAlimento({ ...alimento, tipo: 'personalizado' });
  };
  const abrirEditarAlimentoComun = (alimento) => {
    setEditandoAlimento({ ...alimento, tipo: 'comun' });
  };
  const guardarEdicionAlimento = async () => {
    const nombre = editandoAlimento.nombre.trim();
    let carbs = editandoAlimento.carbsPor100.toString().replace(',', '.');
    carbs = parseFloat(carbs);
    if (nombre === '' || isNaN(carbs) || carbs < 0 || carbs > 100) {
      alert('Introduce un nombre y un valor de HC/100g entre 0 y 100.');
      return;
    }
    if (editandoAlimento.tipo === 'personalizado') {
      const nuevosAlimentos = alimentosPersonalizados.map(a =>
        a.nombre === editandoAlimento.nombre ? { nombre, carbsPor100: carbs } : a
      );
      setAlimentosPersonalizados(nuevosAlimentos);
      await onAddCustomFood({ nombre, carbsPor100: carbs });
      const updatedFoods = await fetchFoods();
      setAlimentosPersonalizados(updatedFoods);
    } else if (editandoAlimento.tipo === 'comun') {
      // Si es común, crear/actualizar alimento personalizado para el usuario
      await onAddCustomFood({ nombre, carbsPor100: carbs });
      const updatedFoods = await fetchFoods();
      setAlimentosPersonalizados(updatedFoods);
    }
    setEditandoAlimento(null);
  };

  // Eliminar alimento común (solo en memoria)
  const eliminarAlimentoComun = (nombre) => {
    if (!window.confirm('¿Seguro que quieres eliminar este alimento?')) return;
    setBorrando(prev => ({ ...prev, [nombre]: true }));
    setTimeout(() => {
      const idx = alimentosComunes.findIndex(a => a.nombre === nombre);
      if (idx !== -1) {
        alimentosComunes.splice(idx, 1);
      }
      // Forzar re-render
      setSortBy(sortBy => sortBy + '');
      setBorrando(prev => {
        const copy = { ...prev };
        delete copy[nombre];
        return copy;
      });
    }, 500);
  };

  // Animación de movimiento para ocultar/mostrar
  const animateRowMove = (nombre, toBottom) => {
    const row = rowRefs.current[nombre];
    if (!row) return;
    row.classList.add(toBottom ? 'move-to-bottom' : 'move-to-top');
    setTimeout(() => {
      row.classList.remove('move-to-bottom', 'move-to-top');
      setMovingFood(null);
    }, 500);
  };

  const handleHideWithAnim = (nombre) => {
    setMovingFood(nombre);
    animateRowMove(nombre, true);
    setTimeout(() => {
      if (onHideCommonFood) onHideCommonFood(nombre);
    }, 500);
  };
  const handleShowWithAnim = (nombre) => {
    setMovingFood(nombre);
    animateRowMove(nombre, false);
    setTimeout(() => {
      if (onShowCommonFood) onShowCommonFood(nombre);
    }, 500);
  };

  // Unir y marcar tipo
  // Si el usuario tiene un alimento personalizado con el mismo nombre y HC/100g igual al común, NO mostrarlo como personalizado
  const nombresPersonalizados = new Set(alimentosPersonalizados.map(a => a.nombre.toLowerCase()));
  // Separar comunes en visibles y ocultos
  const visiblesComunes = alimentosComunes.filter(a => !nombresPersonalizados.has(a.nombre.toLowerCase()) && !hiddenCommonFoods.includes(a.nombre.toLowerCase())).map(a => ({ ...a, tipo: 'comun', oculto: false }));
  const ocultosComunes = alimentosComunes.filter(a => !nombresPersonalizados.has(a.nombre.toLowerCase()) && hiddenCommonFoods.includes(a.nombre.toLowerCase())).map(a => ({ ...a, tipo: 'comun', oculto: true }));
  // Solo mostrar como personalizado si no existe en comunes o si el HC/100g es diferente
  const personalizadosFiltrados = alimentosPersonalizados.map(a => ({ ...a, tipo: 'personalizado', oculto: false }));
  let allFoods = [
    ...visiblesComunes.filter(a => !alimentosPersonalizados.some(ap => ap.nombre.toLowerCase() === a.nombre.toLowerCase())),
    ...personalizadosFiltrados,
    ...ocultosComunes // los ocultos van al final, ordenados
  ];
  // Ordenar
  allFoods = allFoods.sort((a, b) => {
    let vA = a[sortBy];
    let vB = b[sortBy];
    if (sortBy === 'carbsPor100') {
      vA = parseFloat(vA);
      vB = parseFloat(vB);
    } else {
      vA = vA.toString().toLowerCase();
      vB = vB.toString().toLowerCase();
    }
    if (vA < vB) return sortDir === 'asc' ? -1 : 1;
    if (vA > vB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });
  // Cambiar orden
  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto p-6 bg-white rounded-xl shadow-lg">
      {/* Green tick animation o splash de ya guardado */}
      {(showTick === 'saved' || showTick === 'already-exists') && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className={`bg-white bg-opacity-90 rounded-full p-8 shadow-lg animate-fade-tick ${showTick === 'already-exists' ? 'border-blue-300' : 'border-green-300'}`}
            style={{ zIndex: 1002 }}>
            <svg className={`w-24 h-24 ${showTick === 'already-exists' ? 'text-blue-500' : 'text-green-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 13l3 3 6-6" />
            </svg>
            <div className={`text-xl font-bold text-center mt-2 ${showTick === 'already-exists' ? 'text-blue-700' : 'text-green-700'}`}>{showTick === 'already-exists' ? 'Alimento anteriormente guardado en la base de datos' : 'Alimento guardado correctamente'}</div>
          </div>
        </div>
      )}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-center text-indigo-600">Alimentos</h1>
        <div className="mt-2 h-1 w-20 bg-indigo-500 mx-auto rounded-full"></div>
        <p className="mt-3 text-center text-gray-600 text-sm">Consulta y añade alimentos a tu lista personalizada. En los productos embasados, te recomendamos mirar la etiqueta para encontrar los Hidratos de carbono.</p>
      </div>
      <div className="flex justify-end mb-4">
        <button
          onClick={abrirModalNuevoAlimento}
          className="text-sm bg-indigo-500 hover:bg-indigo-600 text-white py-2 px-4 rounded-lg transition-colors shadow-sm flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4 mr-1">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Añadir nuevo alimento
        </button>
      </div>
      <div className="overflow-x-auto alimentos-table-container">
        <table className="alimentos-table min-w-full bg-white border border-gray-200 rounded-lg shadow-sm" style={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <col style={{ width: '33.33%' }} />
            <col style={{ width: '33.33%' }} />
            <col style={{ width: '33.33%' }} />
          </colgroup>
          <thead className="bg-gradient-to-r from-indigo-100 to-purple-100">
            <tr>
              <th className="py-2 px-3 text-xs font-semibold text-gray-600 cursor-pointer select-none text-center align-middle" onClick={() => handleSort('nombre')}>
                Nombre
                {sortBy === 'nombre' && (sortDir === 'asc' ? ' ▲' : ' ▼')}
              </th>
              <th className="py-2 px-3 text-xs font-semibold text-gray-600 cursor-pointer select-none text-center align-middle" onClick={() => handleSort('carbsPor100')}>
                HC/100g
                {sortBy === 'carbsPor100' && (sortDir === 'asc' ? ' ▲' : ' ▼')}
              </th>
              <th className="py-2 px-3 text-xs font-semibold text-gray-600 text-center align-middle" style={{width: '160px'}}>
                Acciones
              </th>
            </tr>
          </thead>
          <tbody>
            {allFoods.map((a, idx) => (
              <tr
                key={a.nombre}
                ref={el => rowRefs.current[a.nombre] = el}
                className={
                  `${a.tipo === 'personalizado' ? 'border-t border-gray-100 bg-indigo-50' : 'border-t border-gray-100'} ` +
                  (a.oculto ? ' text-gray-400 bg-gray-100' : '') +
                  (borrando[a.nombre] ? ' animate-bounce-fade' : '') +
                  (movingFood === a.nombre ? (a.oculto ? ' move-to-bottom' : ' move-to-top') : '')
                }
                style={borrando[a.nombre] ? { pointerEvents: 'none', opacity: 0.3 } : {}}
              >
                <td data-label="Nombre" className={a.tipo === 'personalizado' ? 'py-2 px-3 text-xs text-indigo-700 text-center align-middle alimentos-word-nowrap' : 'py-2 px-3 text-xs text-center align-middle alimentos-word-nowrap'}>
                  {editandoFila === a.nombre ? (
                    <input
                      type="text"
                      className="w-full p-1 border border-gray-300 rounded text-xs"
                      value={editFilaData.nombre}
                      onChange={e => setEditFilaData({ ...editFilaData, nombre: e.target.value })}
                      pattern="[A-Za-zÁÉÍÓÚáéíóúÑñ0-9 .,'-]+"
                    />
                  ) : (
                    a.nombre.split(/(\s+)/).map((part, idx) => part.trim() && !part.includes(' ') ? (
                      <span key={idx} style={{whiteSpace: 'nowrap', display: 'inline-block'}}>{part}</span>
                    ) : part)
                  )}
                </td>
                <td data-label="HC/100g" className={a.tipo === 'personalizado' ? 'py-2 px-3 text-xs text-indigo-700 text-center align-middle alimentos-nowrap' : 'py-2 px-3 text-xs text-center align-middle alimentos-nowrap'}>
                  {editandoFila === a.nombre ? (
                    <input
                      type="text"
                      className="w-full p-1 border border-gray-300 rounded text-xs"
                      value={editFilaData.carbsPor100}
                      onChange={e => {
                        // Permitir solo un número decimal
                        const valor = e.target.value.replace(',', '.');
                        if (/^\d{0,3}(\.\d{0,1})?$/.test(valor) || valor === '') {
                          setEditFilaData({ ...editFilaData, carbsPor100: valor });
                        }
                      }}
                      inputMode="decimal"
                      maxLength={5}
                      placeholder="Ej: 25.5"
                    />
                  ) : (
                    <span className="alimentos-nowrap" style={{display: 'inline-block', minWidth: 28, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{a.carbsPor100}</span>
                  )}
                </td>
                <td data-label="Acciones" className="py-2 px-3 text-xs text-center align-middle" style={{width: '160px', verticalAlign: 'middle'}}>
                  <div className="flex justify-center items-center gap-2 flex-nowrap" style={{minWidth: 0}}>
                    {editandoFila === a.nombre ? (
                      <button
                        className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-xs btn-editar-personalizado"
                        style={{minWidth: 70}}
                        onClick={async () => {
                          const nombre = editFilaData.nombre.trim();
                          let carbs = editFilaData.carbsPor100.toString().replace(',', '.');
                          carbs = parseFloat(carbs);
                          if (nombre === '' || isNaN(carbs) || carbs < 0 || carbs > 100) {
                            alert('Introduce un nombre y un valor de HC/100g entre 0 y 100.');
                            return;
                          }
                          // No guardar si no hay cambios respecto al alimento original
                          if (a.nombre === nombre && Number(a.carbsPor100) === Number(carbs) && a.tipo === 'comun') {
                            setEditandoFila(null);
                            return;
                          }
                          // Subir a Firebase o localStorage según login
                          await onAddCustomFood({ nombre, carbsPor100: carbs });
                          // Pedir al padre que recargue los alimentos personalizados (no recalcular aquí)
                          if (typeof window.fetchFoods === 'function') {
                            const updatedFoods = await window.fetchFoods();
                            setAlimentosPersonalizados(updatedFoods);
                          }
                          setEditandoFila(null);
                        }}
                      >Guardar</button>
                    ) : (
                      <button
                        onClick={() => {
                          setEditandoFila(a.nombre);
                          setEditFilaData({ nombre: a.nombre, carbsPor100: a.carbsPor100 });
                        }}
                        className={`bg-yellow-400 hover:bg-yellow-500 text-white rounded text-xs flex items-center justify-center${a.tipo === 'personalizado' ? ' btn-editar-personalizado' : ''} alimentos-action-btn`}
                        style={{ minWidth: 32, minHeight: 32, width: 32, height: 32, padding: 0 }}
                        aria-label="Editar"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                          <path d="M15.232 5.232l-1.464-1.464a2 2 0 0 0-2.828 0l-6.036 6.036a2 2 0 0 0-.586 1.414V15a1 1 0 0 0 1 1h3.782a2 2 0 0 0 1.414-.586l6.036-6.036a2 2 0 0 0 0-2.828z" />
                          <path d="M13.5 6.5l-7 7" />
                        </svg>
                      </button>
                    )}
                    {a.tipo === 'personalizado' ? (
                      <button
                        onClick={() => eliminarAlimentoPersonalizado(a)}
                        className={`bg-red-500 hover:bg-red-600 text-white rounded text-xs alimentos-action-btn${a.tipo === 'personalizado' ? ' btn-editar-personalizado' : ''}`}
                        style={{ minWidth: 32, minHeight: 32, width: 32, height: 32, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        aria-label="Eliminar"
                      >&#10005;</button>
                    ) : (
                      a.oculto ? (
                        <button
                          onClick={() => onShowCommonFood && onShowCommonFood(a.nombre)}
                          className="bg-green-500 hover:bg-green-600 text-white rounded text-xs alimentos-action-btn"
                          style={{ minWidth: 32, minHeight: 32, width: 32, height: 32, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          aria-label="Mostrar"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width: '16px', height: '16px'}}>
                            <ellipse cx="12" cy="12" rx="10" ry="7" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                      ) : (
                        <button
                          onClick={() => onHideCommonFood && onHideCommonFood(a.nombre)}
                          className="bg-gray-400 hover:bg-gray-500 text-white rounded text-xs alimentos-action-btn"
                          style={{ minWidth: 32, minHeight: 32, width: 32, height: 32, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          aria-label="Ocultar"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width: '16px', height: '16px'}}>
                            <path d="M17.94 17.94A10.06 10.06 0 0 1 12 20c-5.05 0-9.27-3.11-11-7.5a12.35 12.35 0 0 1 4.73-5.73" />
                            <path d="M1 1l22 22" />
                            <path d="M9.53 9.53A3.5 3.5 0 0 0 12 15.5a3.5 3.5 0 0 0 2.47-5.97" />
                            <path d="M14.47 14.47A3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1-2.47 5.97" />
                          </svg>
                        </button>
                      )
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Modal para añadir alimentos personalizados */}
      {modalAbierto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full m-4 relative transform translate-y-0">
            <button 
              onClick={() => setModalAbierto(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Añadir alimento personalizado</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Nombre del alimento</label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 outline-none"
                  value={nuevoAlimento.nombre}
                  onChange={e => setNuevoAlimento({ ...nuevoAlimento, nombre: e.target.value })}
                  placeholder="Ej: Mi receta favorita"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Hidratos de carbono por 100g</label>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 outline-none"
                  value={nuevoAlimento.carbsPor100}
                  onChange={e => {
                    const valor = e.target.value;
                    if (valor === '' || (/^\d{0,3}([.,]\d{0,2})?$/.test(valor) && parseFloat(valor.replace(',', '.')) <= 100)) {
                      setNuevoAlimento({ ...nuevoAlimento, carbsPor100: valor });
                    }
                  }}
                  placeholder="Ej: 25.5"
                />
              </div>
              <div className="pt-2">
                <button
                  onClick={guardarAlimentoPersonalizado}
                  className="w-full bg-indigo-500 hover:bg-indigo-600 text-white py-2 px-4 rounded-lg transition-colors shadow-sm"
                >
                  Guardar alimento
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Modal para editar alimento */}
      {editandoAlimento && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full m-4 relative">
            <button 
              onClick={() => setEditandoAlimento(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Editar alimento</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Nombre del alimento</label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 outline-none"
                  value={editandoAlimento.nombre}
                  onChange={e => setEditandoAlimento({ ...editandoAlimento, nombre: e.target.value })}
                  placeholder="Ej: Mi receta favorita"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Hidratos de carbono por 100g</label>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 outline-none"
                  value={editandoAlimento.carbsPor100}
                  onChange={e => {
                    const valor = e.target.value;
                    if (valor === '' || (/^\d{0,3}([.,]\d{0,2})?$/.test(valor) && parseFloat(valor.replace(',', '.')) <= 100)) {
                      setEditandoAlimento({ ...editandoAlimento, carbsPor100: valor });
                    }
                  }}
                  placeholder="Ej: 25.5"
                />
              </div>
              <div className="pt-2">
                <button
                  onClick={guardarEdicionAlimento}
                  className="w-full bg-indigo-500 hover:bg-indigo-600 text-white py-2 px-4 rounded-lg transition-colors shadow-sm"
                >
                  Guardar cambios
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Diálogo de confirmación de borrado personalizado */}
      {confirmDelete.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full relative z-50">
            <h3 className="text-lg font-bold mb-4 text-gray-800">¿Eliminar alimento?</h3>
            <p className="mb-6 text-gray-600">¿Seguro que quieres eliminar <span className="font-semibold text-indigo-600">{confirmDelete.nombre}</span> de tu lista?</p>
            <div className="flex justify-end gap-3">
              <button onClick={cancelarBorrado} className="px-4 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300">Cancelar</button>
              <button onClick={confirmarBorradoPersonalizado} className="px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600">Eliminar</button>
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes bounce-fade {
          0% { transform: translateY(0); opacity: 1; }
          30% { transform: translateY(-20px); opacity: 1; }
          60% { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(40px); opacity: 0; }
        }
        .animate-bounce-fade {
          animation: bounce-fade 0.5s forwards;
        }
        @keyframes fade-tick {
          0% { opacity: 0; transform: scale(0.8); }
          20% { opacity: 1; transform: scale(1.1); }
          80% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.8); }
        }
        .animate-fade-tick {
          animation: fade-tick 1s cubic-bezier(0.4,0,0.2,1);
        }
        @keyframes moveToBottom {
          0% { transform: translateY(0); opacity: 1; }
          80% { transform: translateY(40px); opacity: 0.5; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes moveToTop {
          0% { transform: translateY(0); opacity: 1; }
          80% { transform: translateY(-40px); opacity: 0.5; }
          100% { transform: translateY(0); opacity: 1; }
        }
        .move-to-bottom {
          animation: moveToBottom 0.5s;
        }
        .move-to-top {
          animation: moveToTop 0.5s;
        }
        @media (max-width: 600px) {
          .alimentos-table-container {
            overflow-x: visible !important;
          }
          .alimentos-table {
            width: 100% !important;
            min-width: 0 !important;
            table-layout: auto !important;
          }
          .alimentos-table th, .alimentos-table td {
            white-space: normal !important;
            word-break: break-word !important;
            min-width: 0 !important;
            max-width: 100vw !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Alimentos;
